"use client";

import { useEffect, useState } from "react";

/**
 * Ref for an infinite-scroll sentinel element.
 *
 * A callback ref rather than the usual `useRef` + `useEffect` pair, and that is
 * the whole point: a sentinel that lives inside a conditional (a tab, an
 * accordion, an empty-state branch) mounts and unmounts long after the paging
 * state last changed, so an effect keyed only on that state either attaches an
 * observer to nothing or keeps watching a node that has since been detached.
 * Storing the node in state instead makes the node itself a dependency, so the
 * observer is created the moment the element appears and torn down the moment
 * it goes away.
 *
 * Usage: `<div ref={useInfiniteScrollRef(hasNextPage, isFetchingNextPage, fetchNextPage)} />`
 */
export function useInfiniteScrollRef(
  hasNextPage: boolean | undefined,
  isFetchingNextPage: boolean,
  fetchNextPage: () => void
) {
  const [node, setNode] = useState<Element | null>(null);

  useEffect(() => {
    if (!node || !hasNextPage) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isFetchingNextPage) fetchNextPage();
      },
      { threshold: 0.1 }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [node, hasNextPage, isFetchingNextPage, fetchNextPage]);

  return setNode;
}
